import { Box, Button, Card, Grid, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useEditOffice } from '@/api/office/edit'
import { useGetOffice } from '@/api/office/get'
import AwarenessScore from '@/common/components/AwarenessScore/AwarenessScore'
import CreateOfficeModal from '@/common/components/CreateOfficeModal/CreateOfficeModal'
import LayoutPageContent from '@/common/components/Layout/LayoutPageContent'
import PageHeader from '@/common/components/Layout/LayoutPageContent/PageHeader'
import LoadingContainer from '@/common/components/LoadingContainer/LoadingContainer'
import NoResultsContainer from '@/common/components/NoResultsContainer/NoResultsContainer'
import UserPerformanceChart, {
  UserPerformanceChartData,
} from '@/common/components/UserPerformanceChart/UserPerformanceChart'
import useToast from '@/common/hooks/useToast'
import { NewOfficeRequest } from '@/types/offices'
import OfficeTables from './OfficeTables'
import { getErrorMessage } from '@/api/utils/get-error'

const OfficeProfile: FC = () => {
  const { _id } = useParams()
  const { data, isLoading } = useGetOffice(_id || '')
  const { mutateAsync: editOffice } = useEditOffice()
  const [editModalIsOpen, setEditModalIsOpen] = useState(false)
  const { t } = useTranslation()
  const { successToast, errorToast } = useToast()

  const openEditOfficeModal = () => setEditModalIsOpen(true)

  const handleEditOffice = async (params: NewOfficeRequest) => {
    try {
      await editOffice({ ...params, _id: _id || '' })
      successToast(t('officeProfile.officeEdited'))
    } catch (e) {
      const errorMessage = getErrorMessage(e) ?? t('officeProfile.errors.faliedEditOffice')
      errorToast(errorMessage)
      throw e
    }
  }

  const chartData = useMemo((): UserPerformanceChartData[] => {
    const failedUsers: UserPerformanceChartData = {
      id: 'failed',
      data: [],
    }
    const passedUsers: UserPerformanceChartData = {
      id: 'passed',
      data: [],
    }

    if (data?.campaign_stats) {
      Object.entries(data.campaign_stats)
        .slice(23)
        .forEach(([key, value]) => {
          failedUsers.data.push({ x: key, y: value.failed })
          passedUsers.data.push({ x: key, y: value.passed })
        })
    }
    return [failedUsers, passedUsers]
  }, [data])

  return (
    <LayoutPageContent minWidth={1000}>
      {/* TODO : create / edit */}
      <CreateOfficeModal
        createNewOffice={() => {}}
        saveOfficeEdit={handleEditOffice}
        open={editModalIsOpen}
        setOpen={setEditModalIsOpen}
        office={data}
      />
      <PageHeader>
        <Typography gutterBottom variant={'h5'} marginLeft={-8}>
          {data ? data.name : ''}
        </Typography>
        <Button color={'inherit'} variant={'outlined'} onClick={openEditOfficeModal}>
          {t('officeProfile.editOffice')}
        </Button>
      </PageHeader>
      <Grid item xs={5}>
        <Card>
          <AwarenessScore score={data?.awareness_score ?? 0} loading={isLoading} />
        </Card>
      </Grid>
      <Grid item xs={7}>
        <Card>
          <Typography gutterBottom variant={'subtitle2'}>
            {t('officeProfile.officePerformance')}
          </Typography>
          <Box height={220}>
            <UserPerformanceChart data={chartData} />
          </Box>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card>
          {isLoading ? (
            <LoadingContainer />
          ) : data ? (
            <OfficeTables currentOfficeId={_id} />
          ) : (
            <NoResultsContainer text={'officeProfile.noResults'} />
          )}
        </Card>
      </Grid>
    </LayoutPageContent>
  )
}

export default OfficeProfile
